// Impact trees: How driver level, specialties, and trainings affect contracts & vehicles

// Specialty synergy: driver specialty → contract category bonuses
export const SPECIALTY_CONTRACT_SYNERGIES = {
  'Messagerie/Colis': {
    'Messagerie / Colis': { revenueBonus: 0.15, label: '+15% revenus', tier: 'excellent' },
    'Vrac/Palettes': { revenueBonus: 0.05, label: '+5% revenus', tier: 'correct' },
  },
  'Vrac/Palettes': {
    'Vrac/Palettes': { revenueBonus: 0.15, label: '+15% revenus', tier: 'excellent' },
    'BTP / Matériaux': { revenueBonus: 0.10, label: '+10% revenus', tier: 'bon' },
    'Messagerie / Colis': { revenueBonus: 0.03, label: '+3% revenus', tier: 'faible' },
  },
  'Frigo/Pharma': {
    'Frigo/Pharma': { revenueBonus: 0.20, label: '+20% revenus', tier: 'excellent' },
    'Messagerie / Colis': { revenueBonus: 0.05, label: '+5% revenus', tier: 'correct' },
  },
  'BTP/Matériaux/Engins TP': {
    'BTP / Matériaux': { revenueBonus: 0.20, label: '+20% revenus', tier: 'excellent' },
    'Vrac/Palettes': { revenueBonus: 0.08, label: '+8% revenus', tier: 'correct' },
  },
  'Longue distance': {
    'Export UE': { revenueBonus: 0.15, label: '+15% revenus', tier: 'excellent' },
    'Vrac/Palettes': { revenueBonus: 0.08, label: '+8% revenus', tier: 'correct' },
    'BTP / Matériaux': { revenueBonus: 0.05, label: '+5% revenus', tier: 'correct' },
  },
  'Export UE': {
    'Export UE': { revenueBonus: 0.25, label: '+25% revenus', tier: 'excellent' },
    'Frigo/Pharma': { revenueBonus: 0.08, label: '+8% revenus (frigo EU)', tier: 'correct' },
  },
  'ADR/Produits sensibles': {
    'Spécialisé': { revenueBonus: 0.25, label: '+25% revenus', tier: 'excellent' },
    'BTP / Matériaux': { revenueBonus: 0.10, label: '+10% revenus', tier: 'bon' },
  },
};

// Level bonuses: cumulative effects per level
export const LEVEL_BONUSES = {
  1: {
    label: 'Débutant',
    color: '#6B7280',
    revenueBonus: 0,
    fuelReduction: 0,
    accidentReduction: 0,
    satisfactionBonus: 0,
    unlocks: ['Permis B uniquement', 'Contrats Tier 1'],
  },
  2: {
    label: 'Confirmé',
    color: '#3B82F6',
    revenueBonus: 0.03,
    fuelReduction: 0.03,
    accidentReduction: 0.02,
    satisfactionBonus: 2,
    unlocks: ['Permis C possible', 'Contrats Tier 2', '+3% revenus contrats'],
  },
  3: {
    label: 'Expérimenté',
    color: '#8B5CF6',
    revenueBonus: 0.06,
    fuelReduction: 0.05,
    accidentReduction: 0.04,
    satisfactionBonus: 5,
    unlocks: ['Permis CE possible', 'Contrats Tier 3', '+6% revenus', '-5% carburant'],
  },
  4: {
    label: 'Senior',
    color: '#F59E0B',
    revenueBonus: 0.10,
    fuelReduction: 0.08,
    accidentReduction: 0.06,
    satisfactionBonus: 8,
    unlocks: ['ADR accessible', 'Contrats Tier 4', '+10% revenus', 'Formation chauffeurs juniors'],
  },
  5: {
    label: 'Expert',
    color: '#EF4444',
    revenueBonus: 0.15,
    fuelReduction: 0.12,
    accidentReduction: 0.08,
    satisfactionBonus: 12,
    unlocks: ['Tous contrats', '+15% revenus', '-12% carburant', 'Bonus réputation +2/mois'],
  },
};

// Vehicle-driver compatibility
export const VEHICLE_DRIVER_COMPATIBILITY = {
  1: { // Tier 1
    requiredLicense: 'B',
    idealLevel: 1,
    levelPenalty: {}, // No penalty, anyone can drive
    specialties: ['Messagerie/Colis', 'Colis', 'Messagerie express'],
  },
  2: { // Tier 2
    requiredLicense: 'C',
    idealLevel: 2,
    levelPenalty: { 1: { wearIncrease: 0.3, label: '+30% usure (inexpérimenté)' } },
    specialties: ['Vrac/Palettes', 'Frigo/Pharma', 'BTP/Matériaux/Engins TP'],
  },
  3: { // Tier 3
    requiredLicense: 'CE',
    idealLevel: 2,
    levelPenalty: { 1: { wearIncrease: 0.5, label: '+50% usure' }, 2: { wearIncrease: 0.1, label: '+10% usure' } },
    specialties: ['Longue distance', 'Frigo/Pharma', 'Vrac/Palettes'],
  },
  4: { // Tier 4
    requiredLicense: 'CE',
    idealLevel: 3,
    levelPenalty: { 1: { wearIncrease: 0.6, label: '+60% usure' }, 2: { wearIncrease: 0.25, label: '+25% usure' } },
    specialties: ['ADR/Produits sensibles', 'Export UE', 'BTP/Matériaux/Engins TP', 'Longue distance'],
  },
  5: { // Tier 5
    requiredLicense: 'CE',
    idealLevel: 4,
    levelPenalty: { 1: { wearIncrease: 0.8, label: '+80% usure' }, 2: { wearIncrease: 0.4, label: '+40% usure' }, 3: { wearIncrease: 0.15, label: '+15% usure' } },
    specialties: ['Export UE', 'ADR/Produits sensibles', 'Longue distance'],
  },
};

// Training impact descriptions
export const TRAINING_IMPACTS = {
  permisC: {
    name: 'Permis C',
    impacts: [
      { type: 'unlock', label: 'Conduite véhicules 3.5T à 32T', icon: 'truck' },
      { type: 'contract', label: 'Accès contrats Tier 2-3', icon: 'file' },
      { type: 'xp', label: '+40 XP', icon: 'star' },
    ],
  },
  permisCE: {
    name: 'Permis CE',
    impacts: [
      { type: 'unlock', label: 'Conduite semi-remorques + remorques', icon: 'truck' },
      { type: 'contract', label: 'Accès contrats Tier 3-5', icon: 'file' },
      { type: 'xp', label: '+60 XP', icon: 'star' },
    ],
  },
  frigo: {
    name: 'Formation Frigorifique',
    impacts: [
      { type: 'bonus', label: '+0.3 compétence', icon: 'trending' },
      { type: 'contract', label: 'Synergies contrats frigo (+20%)', icon: 'file' },
      { type: 'xp', label: '+25 XP', icon: 'star' },
    ],
  },
  adr: {
    name: 'Certification ADR',
    impacts: [
      { type: 'bonus', label: '+0.5 compétence', icon: 'trending' },
      { type: 'contract', label: 'Contrats ADR (+25% revenus)', icon: 'file' },
      { type: 'unlock', label: 'Transport matières dangereuses', icon: 'shield' },
      { type: 'xp', label: '+35 XP', icon: 'star' },
    ],
  },
  ecoConduite: {
    name: 'Éco-conduite',
    impacts: [
      { type: 'bonus', label: '+0.2 compétence', icon: 'trending' },
      { type: 'savings', label: '-10% consommation carburant', icon: 'fuel' },
      { type: 'xp', label: '+15 XP', icon: 'star' },
    ],
  },
  gestionStress: {
    name: 'Gestion du stress',
    impacts: [
      { type: 'bonus', label: '+10 satisfaction, -5 fatigue', icon: 'heart' },
      { type: 'risk', label: 'Risque accident réduit', icon: 'shield' },
      { type: 'xp', label: '+15 XP', icon: 'star' },
    ],
  },
};

// XP thresholds (must match hrSlice)
export const XP_THRESHOLDS = [0, 100, 300, 600, 1000, 1500];
export const getXpForLevel = (level) => XP_THRESHOLDS[level] || XP_THRESHOLDS[XP_THRESHOLDS.length - 1] + (level - XP_THRESHOLDS.length + 1) * 600;
